import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { Navigate } from "react-router-dom";
import { UserPlus, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";
import { ASSIGNABLE_ROLES, CITY_OPS_ROLES, fmtDT } from "../../lib/constants";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";

// Human-readable role + lifecycle-event labels (never expose raw enum labels in the UI).
const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin", CTRL: "City Controller", CTOP: "Control Tower Operator",
  CAP: "Captain", RID: "Rider", ACCOUNTS: "Accounts", SENDER: "Sender",
};
const EVENT_LABELS = {
  USER_INVITED: "User invited",
  USER_INVITE_RESENT: "Invitation resent",
  PASSWORD_SETUP_COMPLETED: "Password setup completed",
  PASSWORD_RESET_REQUESTED: "Password reset requested",
  PASSWORD_RESET_COMPLETED: "Password reset completed",
  PASSWORD_CHANGED: "Password changed",
  SESSIONS_REVOKED: "Sessions revoked",
  USER_DEACTIVATED: "User deactivated",
  USER_REACTIVATED: "User reactivated",
  USER_EMAIL_RECOVERED: "Email recovered",
  OPERATIONAL_USER_REPLACED: "Operational user replaced",
  OPERATIONAL_USER_NAME_CORRECTED: "Full legal name corrected",
  OCCUPANT_REPLACED: "Occupant replaced",
  STAFF_SLOT_VACATED: "Slot vacated",
  OCCUPANT_ASSIGNED: "Occupant assigned",
};
const roleLabel = (r) => ROLE_LABELS[r] || r;
const humanize = (s) => (s || "").toLowerCase().split("_")
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export default function Users() {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState({ email: "", role: "CTRL", full_name: "", airport_code: "" });
  const [busy, setBusy] = useState(false);
  const [airports, setAirports] = useState([]);
  // UL — Super Admin user-detail drawer state.
  const [detailUser, setDetailUser] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");
  const [detailKey, setDetailKey] = useState(0);
  // Bottom user-list tabs (presentation only; same single /admin/users list).
  const [listTab, setListTab] = useState("internal");

  const reload = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(e.displayMessage)).finally(() => setLoading(false));
  };
  useEffect(reload, []);
  useEffect(() => {
    api.get("/master/airports").then((r) => setAirports(r.data)).catch(() => {});
  }, []);

  // Load lifecycle details whenever a user is opened or a lifecycle action bumps detailKey.
  useEffect(() => {
    if (!detailUser) return;
    let cancelled = false;
    setDetailStatus("loading");
    setDetail(null);
    api.get(`/admin/users/${detailUser.id}/details`)
      .then((r) => { if (!cancelled) { setDetail(r.data); setDetailStatus("ok"); } })
      .catch((e) => {
        if (cancelled) return;
        const st = e?.response?.status;
        setDetailStatus(st === 404 ? "notfound" : st === 403 ? "forbidden" : "error");
      });
    return () => { cancelled = true; };
  }, [detailUser, detailKey]);

  const onViewDetails = (u) => setDetailUser(u);
  const closeDetails = () => { setDetailUser(null); setDetail(null); setDetailStatus("idle"); };
  const bumpDetailIf = (id) => { if (detailUser && detailUser.id === id) setDetailKey((k) => k + 1); };

  if (!profile) return null;
  if (profile.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;

  const onInvite = async () => {
    if (!inv.email || !inv.role) return;
    const needsAirport = CITY_OPS_ROLES.includes(inv.role);
    const isOperational = CITY_OPS_ROLES.includes(inv.role);
    if (needsAirport && !inv.airport_code) { toast.error("Select an airport for this city operational role"); return; }
    if (isOperational && !(inv.full_name || "").trim()) { toast.error("Full legal name is required for operational users."); return; }
    setBusy(true);
    try {
      const payload = { email: inv.email, role: inv.role, full_name: (inv.full_name || "").trim() };
      if (needsAirport) payload.airport_code = inv.airport_code;
      await api.post("/admin/users/invite", payload);
      toast.success("User invited");
      setInv({ email: "", role: "CTRL", full_name: "", airport_code: "" });
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };

  const onResendInvite = async (id) => {
    try {
      await api.post(`/admin/users/${id}/resend-invite`);
      toast.success("Invite link resent");
    } catch (e) { toast.error(e.displayMessage); }
  };

  const onDeactivate = async (id) => {
    try {
      await api.post(`/admin/users/${id}/deactivate`, {});
      toast.success("User deactivated — sessions revoked");
      reload();
      bumpDetailIf(id);
    } catch (e) { toast.error(e.displayMessage); }
  };

  const onReactivate = async (id, new_email) => {
    try {
      await api.post(`/admin/users/${id}/reactivate`, { new_email });
      toast.success("User reactivated — set-password link sent");
      reload();
      bumpDetailIf(id);
    } catch (e) { toast.error(e.displayMessage); }
  };

  // B3 — occupant rotation. On success we reload; email-delivery failures surface as a warning
  // (the handover still stands — the incoming occupant can be re-sent credentials).
  const occupantDeliveryToast = (delivery, okMsg) => {
    if (delivery === "FAILED") {
      toast.warning(`${okMsg}, but the credential email could not be sent. Resend it from the user's details.`);
    } else {
      toast.success(okMsg);
    }
  };
  const onReplaceOccupant = async (id, body) => {
    const { data } = await api.post(`/admin/users/${id}/replace-occupant`, body);
    occupantDeliveryToast(data.email_delivery, "Occupant replaced — previous access revoked");
    reload();
    bumpDetailIf(id);
  };
  const onVacate = async (id, reason) => {
    await api.post(`/admin/users/${id}/vacate`, { reason });
    toast.success("Slot vacated — previous access revoked");
    reload();
    bumpDetailIf(id);
  };
  const onAssignOccupant = async (id, body) => {
    const { data } = await api.post(`/admin/users/${id}/assign-occupant`, body);
    occupantDeliveryToast(data.email_delivery, "Occupant assigned");
    reload();
    bumpDetailIf(id);
  };

  const onCorrectName = async (id, new_full_name, reason) => {
    await api.post(`/admin/users/${id}/name`, { new_full_name, reason });
    toast.success("Full legal name corrected");
    reload();
    bumpDetailIf(id);
  };

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-[1200px] mx-auto p-6">
      <h1 className="heading text-3xl mb-1">Users</h1>
      <div className="overline mb-6">SUPER ADMIN ONLY · INVITE STAFF AND CAPTAINS</div>

      <section className="grid-card grid-card-strong p-4 mb-6">
        <div className="overline mb-2">INVITE NEW STAFF</div>
        <div className="flex flex-col md:flex-row md:items-end gap-2">
          <input className="input-field flex-1" placeholder="email@company.com" value={inv.email} onChange={(e) => setInv({...inv, email: e.target.value})} data-testid="invite-email-input" />
          <input className="input-field flex-1" placeholder="Full legal name" value={inv.full_name} onChange={(e) => setInv({...inv, full_name: e.target.value})} data-testid="invite-full-name-input" />
          <select className="input-field md:w-44" value={inv.role} onChange={(e) => setInv({...inv, role: e.target.value, airport_code: ""})} data-testid="invite-role-select">
            {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {CITY_OPS_ROLES.includes(inv.role) && (
            <select className="input-field md:w-40" value={inv.airport_code} onChange={(e) => setInv({...inv, airport_code: e.target.value})} data-testid="invite-airport-select">
              <option value="">Select airport…</option>
              {airports.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.city}</option>)}
            </select>
          )}
          <button className="btn-primary md:w-32 justify-center" onClick={onInvite} disabled={busy || !inv.email || (CITY_OPS_ROLES.includes(inv.role) && (!inv.airport_code || !(inv.full_name || "").trim()))} data-testid="invite-submit-button">
            <UserPlus size={14} /> Invite
          </button>
        </div>
        {CITY_OPS_ROLES.includes(inv.role) && (
          <div className="mt-1 text-xs text-[hsl(var(--muted))]" data-testid="invite-full-name-required-hint">
            Full legal name is required for operational users (as printed on the person's Aadhaar).
          </div>
        )}
        {CITY_OPS_ROLES.includes(inv.role) && (
          <div className="mt-2 text-xs text-[hsl(var(--muted))]" data-testid="city-role-invite-hint">
            City operational roles are bound to one airport. An operational staff ID
            ({inv.role}-{inv.airport_code || "AIRPORT"}-NNN) is generated automatically.
          </div>
        )}
      </section>

      <section className="grid-card">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="heading text-sm">ALL USERS ({users.length})</h3>
          <button className="btn-ghost text-xs" onClick={reload} data-testid="users-reload">
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>
        <UserListTabs users={users} tab={listTab} onChange={setListTab} />
        {loading ? <div className="p-6 mono text-sm">Loading…</div> : (
          <table className="table-air12" id="users-list-panel" role="tabpanel" data-testid={`users-${listTab}-table`}>
            <thead><tr><th>Public ID</th><th>Email</th><th>Name</th><th>Role</th><th>Airport</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {tabUsers(users, listTab).length === 0 && (
                <tr data-testid={`users-${listTab}-empty`}>
                  <td colSpan={7} className="text-sm text-[hsl(var(--muted))]">
                    {listTab === "senders" ? "No senders yet." : "No internal users."}
                  </td>
                </tr>
              )}
              {tabUsers(users, listTab).map((u) => {
                const vacant = (u.occupancy || "OCCUPIED") === "VACANT";
                const rowKey = u.email || u.public_staff_id || u.id;
                return (
                <tr key={u.id} data-testid={`user-row-${rowKey}`}>
                  <td><span className="id-chip text-xs">{u.public_staff_id || u.public_sender_id || "—"}</span></td>
                  <td className={vacant ? "text-xs italic text-[hsl(var(--muted))]" : ""}>
                    {vacant ? "Vacant" : u.email}
                  </td>
                  <td className="text-xs">{vacant ? <span className="italic text-[hsl(var(--muted))]">Vacant</span> : (u.full_name || "—")}</td>
                  <td>
                    <span className="id-chip text-xs">{roleLabel(u.role)}</span>
                  </td>
                  <td className="text-xs">{u.airport_code || "—"}</td>
                  <td className="text-xs">
                    <StatusCell user={u} self={u.id === profile.id} rowKey={rowKey}
                                onDeactivate={onDeactivate} onReactivate={onReactivate} />
                  </td>
                  <td>
                    <ActionsCell user={u} self={u.id === profile.id} rowKey={rowKey}
                                 onResendInvite={onResendInvite} onViewDetails={onViewDetails}
                                 onReplaceOccupant={onReplaceOccupant} onVacate={onVacate}
                                 onAssignOccupant={onAssignOccupant} />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <UserDetailDrawer user={detailUser} status={detailStatus} data={detail} onClose={closeDetails} onCorrectName={onCorrectName} />
    </div>
    </>
  );
}

// Bottom-list partition: every user lands in exactly one tab.
export const tabUsers = (users, tab) =>
  users.filter((u) => (tab === "senders" ? u.role === "SENDER" : u.role !== "SENDER"));

const LIST_TABS = [["internal", "Internal Users"], ["senders", "Senders"]];

function UserListTabs({ users, tab, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 px-4 py-3 border-b" role="tablist" aria-label="User groups" data-testid="users-list-tabs">
      {LIST_TABS.map(([key, label]) => (
        <button key={key} type="button" role="tab" aria-selected={tab === key} aria-controls="users-list-panel"
                className={tab === key ? "btn-primary" : "btn-ghost"} onClick={() => onChange(key)}
                data-testid={`users-tab-${key}`}>
          {label} (<span data-testid={`users-tab-${key}-count`}>{tabUsers(users, key).length}</span>)
        </button>
      ))}
    </div>
  );
}

function ActionsCell({ user, self, rowKey, onResendInvite, onViewDetails, onReplaceOccupant, onVacate, onAssignOccupant }) {
  const inactive = (user.account_status || "ACTIVE") === "INACTIVE";
  const vacant = (user.occupancy || "OCCUPIED") === "VACANT";
  const isStaff = user.role !== "SENDER";
  const isSuperAdmin = user.role === "SUPER_ADMIN";
  const canResend = !self && isStaff && !inactive && !vacant && user.role !== "RID";
  const rotatable = isStaff && !self && !inactive;  // occupant rotation is for staff slots only
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        className="btn-ghost text-[10px] h-6 px-2"
        onClick={() => onViewDetails(user)}
        data-testid={`view-details-btn-${rowKey}`}
      >
        <Eye size={12} /> View details
      </button>
      {canResend && (
        <button
          className="btn-ghost text-[10px] h-6 px-2"
          onClick={() => onResendInvite(user.id)}
          data-testid={`resend-invite-btn-${rowKey}`}
        >
          Resend invite
        </button>
      )}
      {rotatable && vacant && (
        <OccupantDialog mode="assign" user={user} rowKey={rowKey} onSubmit={onAssignOccupant} />
      )}
      {rotatable && !vacant && (
        <OccupantDialog mode="replace" user={user} rowKey={rowKey} onSubmit={onReplaceOccupant} />
      )}
      {rotatable && !vacant && !isSuperAdmin && (
        <VacateDialog user={user} rowKey={rowKey} onSubmit={onVacate} />
      )}
    </div>
  );
}

// B3 — Assign / Replace occupant. The staff slot (ID/role/airport) is immutable; only the
// occupant's Full legal name + login email rotate.
function OccupantDialog({ mode, user, rowKey, onSubmit }) {
  const isAssign = mode === "assign";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = !!name.trim() && !!email.trim() && !!reason.trim() && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await onSubmit(user.id, { full_name: name.trim(), email: email.trim().toLowerCase(), reason: reason.trim() });
      setName(""); setEmail(""); setReason("");
    } catch (e) { toast.error(e.displayMessage || "Action failed"); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="btn-ghost text-[10px] h-6 px-2" data-testid={`${mode}-occupant-btn-${rowKey}`}>
          {isAssign ? "Assign occupant" : "Replace occupant"}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${mode}-occupant-dialog-${rowKey}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAssign ? "Assign occupant to" : "Replace occupant on"} <b>{user.public_staff_id}</b>?
          </AlertDialogTitle>
          <AlertDialogDescription>
            The staff ID (<b>{user.public_staff_id}</b>), role and airport stay unchanged.
            {isAssign
              ? " Enter the new occupant's Full legal name and login email. A set-password link is sent."
              : " The previous occupant's access is revoked immediately. Enter the new occupant's Full legal name and login email; a set-password link is sent to the new address."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <input className="input-field text-sm mt-1" type="text" placeholder="Occupant Full legal name"
               value={name} onChange={(e) => setName(e.target.value)}
               data-testid={`${mode}-occupant-name-${rowKey}`} />
        <input className="input-field text-sm mt-1" type="email" placeholder="occupant-email@company.com"
               value={email} onChange={(e) => setEmail(e.target.value)}
               data-testid={`${mode}-occupant-email-${rowKey}`} />
        <input className="input-field text-sm mt-1" type="text" placeholder="Reason (required)"
               value={reason} onChange={(e) => setReason(e.target.value)}
               data-testid={`${mode}-occupant-reason-${rowKey}`} />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${mode}-occupant-cancel-${rowKey}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!ready} onClick={submit}
                             data-testid={`${mode}-occupant-confirm-${rowKey}`}>
            {busy ? "Working…" : isAssign ? "Assign occupant" : "Replace occupant"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// B3 — Vacate a staff slot (never available for SUPER_ADMIN — not rendered for it).
function VacateDialog({ user, rowKey, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = !!reason.trim() && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await onSubmit(user.id, reason.trim());
      setReason("");
    } catch (e) { toast.error(e.displayMessage || "Action failed"); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="btn-ghost text-[10px] h-6 px-2" data-testid={`vacate-btn-${rowKey}`}>
          Vacate
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`vacate-dialog-${rowKey}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Vacate <b>{user.public_staff_id}</b>?</AlertDialogTitle>
          <AlertDialogDescription>
            The occupant’s access is revoked immediately and the slot becomes <b>Vacant</b>. The staff ID,
            role, airport and shipment assignments are preserved. You can assign a new occupant later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <input className="input-field text-sm mt-1" type="text" placeholder="Reason (required)"
               value={reason} onChange={(e) => setReason(e.target.value)}
               data-testid={`vacate-reason-${rowKey}`} />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`vacate-cancel-${rowKey}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!ready} onClick={submit} data-testid={`vacate-confirm-${rowKey}`}>
            {busy ? "Working…" : "Vacate slot"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============== USER DETAIL DRAWER (SUPER_ADMIN only) ==============

function UserDetailDrawer({ user, status, data, onClose, onCorrectName }) {
  return (
    <Sheet open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="user-detail-drawer">
        <SheetHeader>
          <SheetTitle data-testid="user-detail-title">User details</SheetTitle>
          <SheetDescription>Read-only profile and lifecycle history.</SheetDescription>
        </SheetHeader>
        {status === "loading" && (
          <div className="p-4 mono text-sm" data-testid="user-detail-loading">Loading…</div>
        )}
        {status === "forbidden" && (
          <div className="p-4 text-sm text-[hsl(var(--danger,0_70%_50%))]" data-testid="user-detail-forbidden">
            You do not have permission to view this user.
          </div>
        )}
        {status === "notfound" && (
          <div className="p-4 text-sm text-[hsl(var(--danger,0_70%_50%))]" data-testid="user-detail-notfound">
            This user no longer exists.
          </div>
        )}
        {status === "error" && (
          <div className="p-4 text-sm text-[hsl(var(--danger,0_70%_50%))]" data-testid="user-detail-error">
            Could not load user details. Please close and try again.
          </div>
        )}
        {status === "ok" && data && (
          <div className="mt-4 space-y-6" data-testid="user-detail-content">
            <ProfileSummary p={data.profile} />
            <NameCorrectionForm p={data.profile} onCorrectName={onCorrectName} />
            {data.profile.role === "SENDER" && <CompanyLifecyclePanel user={data.profile} />}
            <LifecycleTimeline events={data.lifecycle_events || []} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// M6 — SUPER_ADMIN Sender-company + GSTIN lifecycle (compact, within the user detail drawer).
function CompanyLifecyclePanel({ user }) {
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/senders/${user.id}/company`);
      setCo(data);
    } catch (e) {
      toast.error(e.displayMessage || "Could not load company");
    } finally {
      setLoading(false);
    }
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const setCompanyStatus = async (deactivate) => {
    let reason = null;
    if (deactivate) {
      reason = window.prompt("Reason for deactivating this company (required):", "");
      if (reason === null) return;
      if (reason.trim().length < 6) { toast.error("A reason of at least 6 characters is required."); return; }
    }
    setBusy(true);
    try {
      const action = deactivate ? "deactivate" : "reactivate";
      await api.post(`/admin/companies/${co.company_id}/${action}`, deactivate ? { reason } : {});
      toast.success(`Company ${deactivate ? "deactivated" : "reactivated"}`);
      await load();
    } catch (e) { toast.error(e.displayMessage || "Action failed"); }
    finally { setBusy(false); }
  };

  const setGstinStatus = async (gstinId, deactivate) => {
    let reason = null;
    if (deactivate) {
      reason = window.prompt("Reason for deactivating this GSTIN (required):", "");
      if (reason === null) return;
      if (reason.trim().length < 6) { toast.error("A reason of at least 6 characters is required."); return; }
    }
    setBusy(true);
    try {
      const action = deactivate ? "deactivate" : "reactivate";
      await api.post(`/admin/company-gstins/${gstinId}/${action}`, deactivate ? { reason } : {});
      toast.success(`GSTIN ${deactivate ? "deactivated" : "reactivated"}`);
      await load();
    } catch (e) { toast.error(e.displayMessage || "Action failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm mono" data-testid="sa-company-loading">Loading company…</div>;
  if (!co || !co.has_company) return <div className="text-sm text-[hsl(var(--muted))]" data-testid="sa-company-none">No company on file.</div>;
  const inactive = (co.status || "ACTIVE") === "INACTIVE";

  return (
    <div className="border rounded-[2px] p-4 space-y-3" data-testid="sa-company-panel">
      <div className="flex items-center gap-2">
        <div className="font-semibold text-sm">{co.legal_name}</div>
        <span className={`ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm ${inactive ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="sa-company-status">
          {inactive ? "Inactive" : "Active"}
        </span>
      </div>
      <div className="text-xs mono text-[hsl(var(--muted))]" data-testid="sa-company-pan">{co.public_company_id} · {co.pan_masked}</div>
      <button className="btn-ghost text-sm" disabled={busy} onClick={() => setCompanyStatus(!inactive)}
              data-testid="sa-company-toggle">
        {inactive ? "Reactivate company" : "Deactivate company"}
      </button>
      <div className="pt-1">
        <div className="overline text-[10px] mb-1.5">GST registrations</div>
        {(co.gstins || []).length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted))]" data-testid="sa-gstin-empty">None recorded.</div>
        ) : (
          <ul className="divide-y" data-testid="sa-gstin-list">
            {co.gstins.map((g) => {
              const gi = (g.status || "ACTIVE") === "INACTIVE";
              return (
                <li key={g.id} className="py-2 flex items-center gap-2" data-testid={`sa-gstin-${g.id}`}>
                  <span className={`font-mono text-sm ${gi ? "line-through text-[hsl(var(--muted))]" : ""}`}>{g.gstin_masked}</span>
                  <button className="btn-ghost text-xs ml-auto" disabled={busy} onClick={() => setGstinStatus(g.id, !gi)}
                          data-testid={`sa-gstin-toggle-${g.id}`}>
                    {gi ? "Reactivate" : "Deactivate"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


const OPERATIONAL_ROLE_SET = ["CTRL", "CTOP", "CAP", "RID"];

function NameCorrectionForm({ p, onCorrectName }) {
  const isOperational = OPERATIONAL_ROLE_SET.includes(p.role) || p.role === "ACCOUNTS";
  const status = p.account_status || "ACTIVE";
  const correctable = status === "ACTIVE" || status === "SETUP_PENDING";
  const vacant = (p.occupancy || "OCCUPIED") === "VACANT";
  const [name, setName] = useState(p.full_name || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(p.full_name || ""); setReason(""); }, [p.id, p.full_name]);
  if (!isOperational || !correctable || vacant) return null;
  const trimmedName = (name || "").trim();
  const trimmedReason = (reason || "").trim();
  const unchanged = trimmedName === (p.full_name || "").trim();
  const canSubmit = !!trimmedName && !!trimmedReason && !unchanged && !busy;
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onCorrectName(p.id, trimmedName, trimmedReason);
      setReason("");
    } catch (e) { toast.error(e.displayMessage || "Could not correct the name"); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="name-correction-form">
      <div className="overline mb-2">CORRECT FULL LEGAL NAME</div>
      <div className="text-xs text-[hsl(var(--muted))] mb-2">
        Current: <span className="font-medium">{p.full_name || "—"}</span>
      </div>
      <input
        className="input-field text-sm mb-2"
        placeholder="Corrected Full legal name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="name-correction-name-input"
      />
      <input
        className="input-field text-sm mb-2"
        placeholder="Reason for correction (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="name-correction-reason-input"
      />
      {unchanged && trimmedName && (
        <div className="text-xs text-[hsl(var(--muted))] mb-2" data-testid="name-correction-unchanged-hint">
          The corrected name is unchanged.
        </div>
      )}
      <button
        className="btn-primary text-xs h-8 px-3 justify-center"
        disabled={!canSubmit}
        onClick={submit}
        data-testid="name-correction-submit"
      >
        {busy ? "Saving…" : "Save correction"}
      </button>
    </section>
  );
}

function ProfileSummary({ p }) {
  const vacant = (p.occupancy || "OCCUPIED") === "VACANT";
  const statusLabel = vacant ? "Vacant slot"
    : ({ ACTIVE: "Active", INACTIVE: "Inactive", SETUP_PENDING: "Invitation pending" }[p.account_status] || p.account_status);
  const rows = [
    ["Full name", vacant ? "Vacant" : (p.full_name || "—")],
    ["Login email", vacant ? "Vacant" : (p.email || "—")],
    ["Role", roleLabel(p.role)],
    p.public_staff_id ? ["Staff ID", p.public_staff_id] : null,
    p.public_sender_id ? ["Sender ID", p.public_sender_id] : null,
    p.airport_code ? ["Airport / City", p.airport_code] : null,
    ["Account status", statusLabel],
    ["Created", fmtDT(p.created_at)],
  ].filter(Boolean);
  return (
    <section data-testid="user-detail-profile">
      <div className="overline mb-2">PROFILE</div>
      <dl className="space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-sm">
            <dt className="text-[hsl(var(--muted))]">{k}</dt>
            <dd className="text-right font-medium break-all">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function LifecycleTimeline({ events }) {
  return (
    <section data-testid="user-detail-timeline">
      <div className="overline mb-2">LIFECYCLE HISTORY</div>
      {events.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted))]" data-testid="user-detail-no-history">
          No lifecycle history yet.
        </div>
      ) : (
        <ol className="space-y-3">
          {events.map((e, i) => <LifecycleItem key={i} e={e} />)}
        </ol>
      )}
    </section>
  );
}

function LifecycleItem({ e }) {
  const label = EVENT_LABELS[e.event_type] || humanize(e.event_type);
  const actor = e.actor;
  const actorText = actor
    ? (actor.name || actor.email || actor.staff_id || "—")
    : (e.actor_role ? roleLabel(e.actor_role) : "System");
  const outcome = buildOutcome(e, e.details || {});
  return (
    <li className="border-l-2 pl-3 py-0.5" data-testid={`lifecycle-event-${e.event_type}`}>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-[hsl(var(--muted))]">{fmtDT(e.occurred_at)}</div>
      <div className="text-xs mt-0.5">By {actorText}</div>
      {outcome && <div className="text-xs mt-0.5 text-[hsl(var(--muted))]">{outcome}</div>}
    </li>
  );
}

// Build a factual, human-readable outcome line from whitelisted detail fields only.
function buildOutcome(e, d) {
  const parts = [];
  if (e.event_type === "USER_EMAIL_RECOVERED" && d.old_email && d.new_email) {
    parts.push(`Email changed from ${d.old_email} to ${d.new_email}`);
  }
  if (e.event_type === "USER_REACTIVATED") {
    if (d.mode === "EMAIL_REPLACED") parts.push("Reactivated with a new email");
    else if (d.mode === "SAME_EMAIL") parts.push("Reactivated with the same email");
    if (d.old_email_retired) parts.push(`Previous email retired: ${d.old_email_retired}`);
  }
  if (e.event_type === "USER_INVITED" && d.role) {
    parts.push(`Invited as ${roleLabel(d.role)}${d.airport_code ? " at " + d.airport_code : ""}`);
  }
  if (e.event_type === "OCCUPANT_REPLACED") {
    if (d.outgoing_name || d.outgoing_email) parts.push(`Previous occupant: ${d.outgoing_name || d.outgoing_email}`);
    if (d.incoming_name || d.incoming_email) parts.push(`New occupant: ${d.incoming_name || d.incoming_email}`);
  }
  if (e.event_type === "OCCUPANT_ASSIGNED" && (d.incoming_name || d.incoming_email)) {
    parts.push(`Occupant: ${d.incoming_name || d.incoming_email}`);
  }
  if (e.event_type === "STAFF_SLOT_VACATED" && (d.outgoing_name || d.outgoing_email)) {
    parts.push(`Vacated by removing: ${d.outgoing_name || d.outgoing_email}`);
  }
  if (e.event_type === "OPERATIONAL_USER_NAME_CORRECTED" && d.corrected_full_name) {
    parts.push(`Name corrected to ${d.corrected_full_name}`);
  }
  if (d.sessions_revoked === true) parts.push("All sessions revoked");
  if (e.event_type === "SESSIONS_REVOKED" && d.context) {
    if (d.context === "PASSWORD_CHANGE") parts.push("Triggered by a password change");
    else if (d.context === "REACTIVATION") parts.push("Triggered by reactivation");
  }
  if (e.reason) parts.push(`Reason: ${e.reason}`);
  return parts.join(" · ");
}

function StatusCell({ user, self, rowKey, onDeactivate, onReactivate }) {
  const status = user.account_status || "ACTIVE";
  const vacant = (user.occupancy || "OCCUPIED") === "VACANT";
  const inactive = status === "INACTIVE";
  const pending = status === "SETUP_PENDING";
  const label = vacant ? "Vacant" : pending ? "Invitation pending" : status === "ACTIVE" ? "Active" : "Inactive";
  const [newEmail, setNewEmail] = useState(user.email || "");
  useEffect(() => { setNewEmail(user.email || ""); }, [user.email]);
  const reactivateReady = !!newEmail.trim();
  // A VACANT slot has no occupant to deactivate/reactivate — occupant actions live in the Actions column.
  if (vacant) {
    return (
      <div className="flex items-center gap-2" data-testid={`status-cell-${rowKey}`}>
        <span className="id-chip text-xs italic text-[hsl(var(--muted))]" data-testid={`status-badge-${rowKey}`}>
          {label}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`status-cell-${rowKey}`}>
      <span
        className={`id-chip text-xs ${inactive ? "text-[hsl(var(--danger,0_70%_50%))]" : pending ? "text-amber-600" : ""}`}
        data-testid={`status-badge-${rowKey}`}
      >
        {label}
      </span>
      {inactive ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="btn-ghost text-[10px] h-6 px-2" data-testid={`reactivate-btn-${rowKey}`}>
              Reactivate
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`reactivate-dialog-${rowKey}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Reactivate {user.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                Reactivation restores the <b>same person</b> to this slot and keeps the same email.
                Previous sessions stay revoked and a set-password link is sent so they can sign in again.
                To install a <b>different</b> person, cancel and use <b>Replace Occupant</b> instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <input
              className="input-field text-sm mt-1"
              type="email"
              placeholder="account email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              data-testid={`reactivate-email-input-${rowKey}`}
            />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`reactivate-cancel-${rowKey}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={!reactivateReady}
                onClick={() => onReactivate(user.id, newEmail.trim())}
                data-testid={`reactivate-confirm-${rowKey}`}
              >
                Reactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : self ? (
        <span className="text-[10px] text-[hsl(var(--muted))]">(you)</span>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="btn-ghost text-[10px] h-6 px-2" data-testid={`deactivate-btn-${rowKey}`}>
              Deactivate
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`deactivate-dialog-${rowKey}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate {user.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                All active sessions are revoked immediately and the user cannot log in.
                Historical audit records are preserved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`deactivate-cancel-${rowKey}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDeactivate(user.id)}
                data-testid={`deactivate-confirm-${rowKey}`}
              >
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}


